import { z } from 'zod';
// --- §4.1 Categorization ---
export const ANIMALS = ['bird', 'reptile', 'mammal', 'fish', 'insect'];
export const ELEMENTS = ['wood', 'fire', 'earth', 'wind', 'water'];
export const TIERS = [1, 2, 3, 4, 5];
export const STARS = [1, 2, 3];
export const AnimalSchema = z.enum(ANIMALS);
export const ElementSchema = z.enum(ELEMENTS);
export const TierSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]);
export const StarSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
// --- §4.5 Triggers ---
export const TRIGGERS = [
    // Battle-phase triggers — fire while a battle is being simulated.
    'on_unfold', // battle starts (SAP on_battle_start)
    'on_strike', // this unit strikes the frontline
    'on_creased', // takes damage but does not faint
    'on_crumple', // HP reaches 0
    'on_ally_crumple', // any ally crumples
    'on_ally_strike', // an ally in front of this unit strikes
    'on_ally_in_front_strike', // the ally DIRECTLY ahead of this unit
    // strikes (more restrictive than
    // on_ally_strike — fires only for the
    // observer at striker's index+1)
    'on_ally_unfolded', // a new ally is summoned mid-battle
    // Shop-phase triggers — fire during shop, not battle. May modify
    // gold, the shop snapshot, the team, base stats, and the bench.
    // MUST NOT modify in-flight battle state. Within a single shop
    // event the unit that caused it resolves first; observers fire
    // active-line order (1–5) then bench order (1–3). See spec §4.5.
    'on_refold', // this unit reaches a new star level via combining;
    //   also fires `on_combine_witnessed` on every other team unit
    'on_combine_witnessed', // any other unit on the team star-ups
    'on_shop_open', // shop phase begins (after auto-reroll); does NOT fire on manual rerolls
    'on_purchase', // a unit is bought; scope: 'self' or 'any' (default 'self')
    'on_sell', // a unit is sold; scope: 'self' or 'any' (default 'self')
    'on_reroll', // the player manually rerolls the shop (observers only)
    'on_shop_round_pass', // a new shop round begins (post-auto-reroll). Owning
    //   units iterate the freshly-rolled slot row and
    //   can mutate locked-slot buffs (sage_owl pattern)
    'passive', // always-on aura/modifier
];
export const TriggerSchema = z.enum(TRIGGERS);
// --- §4.5 Effects ---
export const EFFECT_TYPES = [
    'deal_damage',
    'buff_stat',
    'heal',
    'summon',
    'swap_positions',
    'gain_status',
    'damage_reduction', // passive mitigation (e.g., tortoise plate)
    'bench_modifier', // shop-phase passive (e.g., "ignores_bench_slot",
    // "round_start_while_benched" stat growth, etc.)
    // Shop-phase effects (do not run inside the battle simulator;
    // dispatched by the shop state machine when its trigger fires).
    'shop_guarantee',
    'shop_grant_free_reroll',
    'grant_gold', // award gold (now or next round per timing param).
    // Bi-phasic: shop-phase fires (e.g. mantis_acolyte
    // on_refold) mutate ShopState directly; battle-phase
    // fires (e.g. gold_fish on_crumple) emit a
    // gold_earned event that the post-battle pipeline
    // applies to pendingGold.
    'consume_unit', // shop-phase: at round-pass, eat a lineup neighbor
    // matching a filter and absorb a ratio of its stats.
    // Wolf Spider's only user today.
    'gold_tribute_buff', // shop-phase: at round-pass, pay N gold (gated by
    // affordability) to permanently buff self.
    // Banky Pig's only user today.
    'sell_value_from_hp', // shop-phase: on_sell, override default tier*star
    // refund with floor(self.max_hp / divisor).
    // Banky Pig's only user today.
];
export const EffectTypeSchema = z.enum(EFFECT_TYPES);
// --- §7.7 Elemental matchup ---
export const MATCHUP_MULTIPLIERS = [0.75, 1.0, 1.25];
//# sourceMappingURL=enums.js.map